import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
// import ProductAddToCartButton, { // Store Custom Code
//    ProductAddToCartButtonProps, // Store Custom Code
//    useShowProductAddToCartButton, // Store Custom Code
// } from "@store/Overrides/Components/ProductAddToCartButton"; // Store Custom Code
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@store/Framework/Types/Button";
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import constants from "@store/constants";
import { useCurrentPage } from "@store/Framework/Store/Data/Pages/PageHooks";
import Button from "@store/MobiusOverrides/Button/Button";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
import { connect } from "react-redux";
import { useProductPackSize } from "@store/Library/Hooks/useProductPackSize";
import Typography, { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";

type Props = AddToCartOptionsAndButtonProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState, props: AddToCartOptionsAndButtonProps & HasProductContext) => {
    return {
        productDetailPath: props.productContext.productInfo.productDetailPath,
    };
};
/* Store Custom Code - End */

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
    isDiscontinuedCustom?: string; // Store Custom Code
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: any;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
    multipleQtyMessage?: TypographyProps;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
        css: css`
            width: 100%;
        `,
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    width: 100%;
                `,
            },
        },
    },
    quantityOrdered: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1;
                    width: 100%;
                    align-self: flex-end;
                `,
            },
        },
    },
    // Store Custom Code
    multipleQtyMessage: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
};

const AddToCartOptionsAndButton: FC<Props> = ({
    /*  Store Custom Code  */
    extendedStyles,
    overridedStyles,
    sizeVariant = "small",
    buttonSizeVariant: baseButtonSizeVariant,
    showUnitOfMeasure = true,
    productDetailPath, // Store Custom Code
    productContext: {product: {isDiscontinued, ...product}, productInfo},
    isDiscontinuedCustom, // Store Custom Code
}) => {
    const currentPage = useCurrentPage();
    const isProductDetailsPage = currentPage.type === constants.pageType.productDetails;
    const isProductListPage = currentPage.type === constants.pageType.productList;

    const { isMultiple, messageText } = useProductPackSize(
        { ...product, pricing: productInfo.pricing, detailName: product.detail?.name }, // Store Custom Code
        { enabled: isProductDetailsPage },
    );

    let multipleQuantitiesMessage: React.ReactNode | null = null;
    if (isMultiple && messageText) {
        multipleQuantitiesMessage = <Typography {...addToCartOptionsAndButtonStyles.multipleQtyMessage}>{messageText}</Typography>;
    }

    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                },
            },
            addToCartButton: {
                extendedStyles: {
                button: {
                        /*  Store Custom Code - Start */
                        variant: "secondary",
                        sizeVariant: buttonSizeVariant,
                        css: css`
                            display:inline-flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: ${x => x.theme.design.typography.weight.bold};
                        `,
                        /* Store Custom Code - End */
                    },
                },
            },
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant]);

    const showProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductQuantityOrdered = useShowProductQuantityOrdered(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    /* Store Custom Code - Start */
    if (!showProductAddToCartButton && !isProductDetailsPage) {
        return (
            <FlexWrapper wrap={false} {...styles.flexWrapper}>
                <Button {...styles.addToCartButton?.extendedStyles?.button} href={productDetailPath}>
                    {translate("View More")}
                </Button>
            </FlexWrapper>
        );
    }

    return (
        <div style={{width: '100%'}}>
            {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
                {!isProductListPage ? (
                    <ProductQuantityOrdered 
                        {...styles.quantityOrdered}
                        multipleQtyMessage={multipleQuantitiesMessage}
                        rightContent={<ProductAddToCartButton isDiscontinuedCustom={isDiscontinuedCustom} {...styles.addToCartButton} />}
                    />
                ) : (
                    <ProductAddToCartButton isDiscontinuedCustom={isDiscontinuedCustom}{...styles.addToCartButton} />
                )}
        </div>
    );
    /* Store Custom Code - End */
};

export default withProductContext(connect(mapStateToProps)(AddToCartOptionsAndButton)); // Store Custom Code
